import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateApiToken } from '../api';
import { useCreateApiTokenResult } from './useCreateApiTokenResult';
import type {
  CreateApiTokenRequestDto,
  CreateApiTokenResponseDto,
} from './types';
import { useTwoFactorAction } from '@/entities/mfa-identity';

type Props = {
  accountNumber: string;
  onBack: () => void;
};

type CreateApiTokenError = {
  accountNumber: string;
  message: string;
};

export const useCreateApiTokenFlow = ({ accountNumber, onBack }: Props) => {
  const { t } = useTranslation();
  const { goToSuccessScreen } = useCreateApiTokenResult();
  const { mutateAsync: createTokenAsync, isPending: isCreateApiTokenPending } =
    useCreateApiToken({ throwOnError: false });
  const [error, setError] = useState<CreateApiTokenError | null>(null);

  const { execute } = useTwoFactorAction<
    CreateApiTokenResponseDto,
    CreateApiTokenRequestDto
  >({
    requestFn: ({ accountNumber, twoFA }) =>
      createTokenAsync({ accountNumber, twoFA }),
    onBack,
    onSuccess: response =>
      response &&
      goToSuccessScreen({
        apiToken: response,
        onBack,
      }),
    onError: err =>
      setError({
        accountNumber,
        message:
          (typeof err === 'string' ? err : err.message) ||
          t('common.unknownError'),
      }),
  });

  const createApiToken = useCallback(() => {
    setError(null);
    execute({ accountNumber, twoFA: null });
  }, [execute, accountNumber]);

  return {
    createApiToken,
    isCreateApiTokenPending,
    createApiTokenError:
      error?.accountNumber === accountNumber ? error.message : null,
  };
};
